import type {
	ChatInputMessage,
	ChatMessage,
	ToolApprovalRequestContentBlock,
} from '#layers/ai/types/chat';
import type { AiContextItem } from '#layers/ai/types/context';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { applyChatStreamEvent } from '#layers/ai/utils/apply-chat-stream-event';
import { streamChat } from '#layers/ai/utils/stream-chat';
import { formatError, useWorkspaceApiBaseUrlFactory } from '#layers/base/public';
import { useWorkspaceRuntimeHandle } from '#layers/runtime/public';
import { useWorkspaceStore } from '#layers/workspace/public';

type ChatStatus = 'submitted' | 'streaming' | 'approvalRequired' | 'ready' | 'error';

export const useChatStore = defineStore('ai-chat', () => {
	const i18n = useI18n();
	const createWorkspaceBaseUrl = useWorkspaceApiBaseUrlFactory();

	const messages = shallowRef<ChatMessage[]>([]);
	const status = ref<ChatStatus>('ready');
	const contentBlockCount = ref(0);
	const errors = shallowRef<string[]>([]);
	const activeRequestController = shallowRef<AbortController | null>(null);
	const contextItems = ref(new Map<string, AiContextItem[]>());

	// The in-flight stream dies with its runtime's signal; the transition only clears the transcript.
	useWorkspaceStore().onLifecycleTransition(clearTranscript);

	const context = computed(() => {
		const itemsById = new Map<string, AiContextItem>();

		// Deduplicates context items by ID. Context providers should use unique context IDs
		for (const items of contextItems.value.values()) {
			for (const item of items) {
				itemsById.set(item.id, item);
			}
		}

		return [...itemsById.values()];
	});

	const pendingApproval = computed<ToolApprovalRequestContentBlock | null>(() => {
		const finalMessage = messages.value.at(-1);
		const finalBlock = finalMessage?.content.at(-1);

		if (finalBlock?.type !== 'toolApprovalRequest') return null;

		return finalBlock;
	});

	const isBusy = computed(() => status.value === 'submitted' || status.value === 'streaming');

	const canSendMessage = computed(() => {
		if (status.value === 'ready') return true;
		if (status.value !== 'error') return false;

		// A failed approval resume leaves the response at the end of the transcript.
		// Adding a new user message would make that response non-final and could make a retry unsafe.
		return getFinalApprovalResponseMessage(messages.value) === undefined;
	});

	function setContext(id: string, items: AiContextItem[]) {
		contextItems.value = new Map(contextItems.value).set(id, items);
	}

	function removeContext(id: string) {
		const next = new Map(contextItems.value);
		next.delete(id);
		contextItems.value = next;
	}

	async function streamAssistantResponse() {
		// The store outlives runtimes; the stream binds to the one active when it starts.
		const handle = useWorkspaceRuntimeHandle();
		const stopController = new AbortController();
		const isCurrent = () => activeRequestController.value === stopController && handle.isActive();
		const approvalResponseMessage = getFinalApprovalResponseMessage(messages.value);
		const approvalRequestMessage = approvalResponseMessage ? messages.value.at(-2) : undefined;
		const isApprovalResume = approvalResponseMessage !== undefined;

		try {
			activeRequestController.value = stopController;
			status.value = 'streaming';

			for await (const streamEvent of streamChat(messages.value, context.value, {
				baseURL: createWorkspaceBaseUrl(handle.slug),
				signal: AbortSignal.any([handle.signal, stopController.signal]),
			})) {
				if (!isCurrent()) return;

				if (streamEvent !== '[DONE]' && streamEvent.type === 'contentBlockStart') {
					contentBlockCount.value = contentBlockCount.value + 1;
				}

				messages.value = applyChatStreamEvent(messages.value, streamEvent);
			}

			if (!isCurrent()) return;

			if (isApprovalResume) {
				messages.value = messages.value.filter((message) =>
					message !== approvalRequestMessage
					&& message !== approvalResponseMessage,
				);
			}

			status.value = pendingApproval.value ? 'approvalRequired' : 'ready';
		}
		catch (error) {
			if (isCurrent()) {
				if (isAbortError(error)) {
					status.value = isApprovalResume ? 'error' : 'ready';
					return;
				}

				status.value = 'error';
				errors.value = [...errors.value, formatError(error, i18n)];
			}
		}
		finally {
			if (activeRequestController.value === stopController) {
				activeRequestController.value = null;
			}
		}
	}

	async function sendMessage(text: string) {
		if (!canSendMessage.value) return;

		const newMessage: ChatInputMessage = {
			role: 'user',
			content: [
				{
					type: 'text',
					text,
				},
			],
		};

		messages.value = [...messages.value, newMessage];
		errors.value = [];
		status.value = 'submitted';

		await streamAssistantResponse();
	}

	async function approveToolApproval() {
		await respondToToolApproval({ approved: true });
	}

	async function denyToolApproval(reason?: string) {
		await respondToToolApproval({
			approved: false,
			...(reason ? { reason } : {}),
		});
	}

	async function respondToToolApproval(
		response: { approved: boolean; reason?: string },
	) {
		const request = pendingApproval.value;

		if (status.value !== 'approvalRequired' || !request) return;

		messages.value = [
			...messages.value,
			{
				role: 'user',
				content: [
					{
						type: 'toolApprovalResponse',
						toolCallId: request.toolCallId,
						...response,
					},
				],
			},
		];

		status.value = 'submitted';

		await streamAssistantResponse();
	}

	function stop() {
		activeRequestController.value?.abort();
	}

	function reset() {
		stop();
		clearTranscript();
	}

	function clearTranscript() {
		messages.value = [];
		status.value = 'ready';
		contentBlockCount.value = 0;
		errors.value = [];
		activeRequestController.value = null;
	}

	return {
		canSendMessage,
		contentBlockCount,
		approveToolApproval,
		denyToolApproval,
		errors,
		isBusy,
		messages,
		pendingApproval,
		sendMessage,
		status,
		stop,
		context,
		setContext,
		removeContext,
		reset,
	};
});

function getFinalApprovalResponseMessage(messages: ChatMessage[]): ChatMessage | undefined {
	const finalMessage = messages.at(-1);
	const finalBlock = finalMessage?.content.at(-1);

	if (finalBlock?.type !== 'toolApprovalResponse') return undefined;

	return finalMessage;
}

function isAbortError(error: unknown): boolean {
	return (
		(error instanceof DOMException && error.name === 'AbortError')
		|| (error instanceof Error && error.name === 'AbortError')
	);
}
